// ACTION ITEMS — the work someone owes, either way, as the mock lists it, and
// one item's popup and history.
//
// ⭐ PURE, AND THE PAGE PRINTS WHAT IT RETURNS. The items, their audit rows and
// the reader's brands come in already read (lib/server/client-slack-action-
// items.ts); this decides which item is under which tab, in what order, and
// every word about it, the History tab's included. Nothing here reads.
//
// ⭐ AN ITEM'S STATUS IS A PERSON'S, OR THE MODEL'S SUGGESTION UNTIL ONE CONFIRMS:
// `status` NULL means nobody has confirmed one, and the page shows the model's
// `suggestedStatus` as a dashed chip with "✦ suggested". Which tab an item sits
// under follows the same rule (`effectiveStatus`), so an item the model says is
// done is under Done, dashed, until someone confirms.
// Once a person has set one, that is the end of the model's say on it: the
// store clears the suggestion and the job writes no new one, and nothing here
// would draw an old one.
//
// ⭐ AN OPEN ITEM IS UNDER WHOEVER HAS THE BALL: We owe or Client owes, by
// `owes`, which the ledger moves with each turn since items v4
// (lib/server/action-items.ts `refreshLedgerBall`). ⚠️ A CLOSED ITEM KEEPS ITS
// LAST BALL (the column is NOT NULL), so every read of the ball here asks
// whether the item is open first (`ballOf`), and a closed row shows none.
//
// ⭐ A STANDING RULE IS UNDER ONGOING, NOT A BALL (nobody owes a standing rule):
// an open item typed `standing_instruction` is under Ongoing and off We owe and
// Client owes, and its row shows no ball, whatever the column holds
// (`openTabOf`). Done when the rule stops applying.
//
// ⭐ THE HISTORY TAB IS audit_event AND NOTHING ELSE: the store writes one row
// per changed field, in the change's transaction (lib/server/action-items.ts).
// One act writes several rows with one actor and one `at`; they are one entry
// here. The model's rows are by ACTION_ITEM_MODEL_EMAIL: "✦ Model".
//
// ⭐ ITS WORK TYPE IS A COLUMN: the Work Type skill's kind or a person's
// (lib/action-item-kinds.ts), and a meeting's or a standing rule's is their
// type's, so the column folds in the Standing/Scheduling tag where it shows. No
// kind yet is `—`. A kind decides no tab, ball or status.
//
// Times: an item's are ours, in ms; `now` is ms too.

import type { FooterNote } from "@/components/dashboards/dashboard-footer";
import {
  ACTION_ITEM_MODEL_EMAIL,
  BRAND_KIND,
  ESCALATION_KIND,
  ITEMS_CHECK_ADDED_BY,
  INACTIVE_KIND,
  MESSAGES_KIND,
  PERSON_FIELDS,
  RECHECK_KIND,
  STATUS_WORDS,
  SUGGESTION_KIND,
  isClosedStatus,
  type ActionItemStatus,
  type Owes,
} from "@/lib/action-items";
import { KIND_WORDS, shownKind, type WorkKind } from "@/lib/action-item-kinds";
import type { ItemType } from "@/lib/client-slack-items-v3";
import { dur, slackScopePills, type RtBrand, type RtPill } from "@/lib/client-slack-response-times";

export const ACTION_ITEMS_PATH = "/brands/action-items";

/** An item, as the page needs it: lib/server/action-items.ts' `ActionItem`. */
export interface ItemRecord {
  origins?: Array<"Slack" | "Fathom" | "Manual">;
  lastActivityAt?: number | null;
  id: string;
  brandSlug: string;
  title: string;
  description: string;
  status: ActionItemStatus | null;
  suggestedStatus: ActionItemStatus | null;
  suggestedWhy: string | null;
  owes: Owes;
  assigneeEmail: string | null;
  due: string | null;
  notes: string;
  escalation: boolean;
  archived: boolean;
  source: "model" | "manual";
  createdBy: string;
  createdAt: number;
  lastSlackAt: number | null;
  /** Its type: the model's, or a person's; none is a task. */
  type?: ItemType;
  /** Its work type (B2b): the Work Type skill's or a person's; null or absent: none yet. */
  kind?: WorkKind | null;
  /** Who said it; absent or null: nobody yet. */
  kindBy?: "model" | "person" | null;
  /** Marked inactive by the re-check, and nothing newer has borne on it: off every open list. */
  inactive?: { at: number; quietDays: number; since: number; why: string } | null;
  /** The re-check's ending or merge, while it holds: its reason beside the suggestion. */
  recheck?: { verdict: "ended" | "merged" | "not_work"; reason: string; at: number } | null;
  /** A person re-opened it after it went inactive (ms). */
  reopenedAt?: number | null;
}

/** Inactive (X4): the re-check found only silence past the expiry line, and nobody has closed it since. Not done, not open work. */
export const isInactive = (it: Pick<ItemRecord, "inactive" | "status" | "suggestedStatus">): boolean => (it.inactive ?? null) !== null && isOpenStatus(it);
/** The status word an inactive item shows. */
export const INACTIVE_WORD = "Inactive";
/** "Recently inactive" holds an item this many days, then it is under All only. */
export const RECENTLY_INACTIVE_DAYS = 14;

/**
 * ⚠️ SCHEDULING SETTLED IS KEPT, NOT SHOWN AS WORK: the model lists "arrange a
 * call time" so a later message can settle it. An OPEN one is work, under
 * whoever owes the answer and in the counts (a reschedule nobody answered is
 * still owed); a done or dropped one is off Done and shows only under All (and
 * Archived) with its tag.
 */
/** The model's reason as a sentence of its own ("we promised it" → "We promised it"). */
export const capitalised = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
export const isWork = (it: Pick<ItemRecord, "type">): boolean => it.type !== "scheduling";
/** The row's tag for an item that isn't a one-off task. */
export const TYPE_TAG: Partial<Record<ItemType, string>> = { scheduling: "Scheduling", standing_instruction: "Standing" };
/** Each type in the form's and the History tab's words (a person can type an item). */
export const TYPE_WORDS: Record<ItemType, string> = { task: "Task", standing_instruction: "Standing rule", scheduling: "Scheduling" };
/** A standing rule for us to follow: under Ongoing while open, and nobody owes on it. */
export const isStanding = (it: Pick<ItemRecord, "type">): boolean => it.type === "standing_instruction";

// ─── The status ──────────────────────────────────────────────────────────────

/** The status an item is under: a person's, else the model's suggestion, else To do. */
export const effectiveStatus = (it: Pick<ItemRecord, "status" | "suggestedStatus">): ActionItemStatus => it.status ?? it.suggestedStatus ?? "not_started";
/** Open: to do or in progress; not done or dropped. */
export const isOpenStatus = (it: Pick<ItemRecord, "status" | "suggestedStatus">): boolean => !isClosedStatus(effectiveStatus(it));
/** Who has the ball on an open item: `owes`. Null on a closed item, which keeps its last ball in the column but owes nothing. */
export const ballOf = (it: Pick<ItemRecord, "status" | "suggestedStatus" | "owes">): Owes | null => (isOpenStatus(it) ? it.owes : null);
/**
 * ⭐ WHICH OPEN TAB AN ITEM IS UNDER: Ongoing for a standing rule, else its
 * ball's (`ballOf`); null on a closed item. The page's tabs and rows, and the
 * Weekly Report's heads (lib/weekly-report-action-items.ts), all read this.
 */
export type OpenTab = Owes | "ongoing";
export const openTabOf = (it: Pick<ItemRecord, "status" | "suggestedStatus" | "owes" | "type" | "inactive">): OpenTab | null =>
  !isOpenStatus(it) || isInactive(it) ? null : isStanding(it) ? "ongoing" : ballOf(it);
/** The ball a row shows: none on a closed item or a standing rule. */
const rowBall = (it: Pick<ItemRecord, "status" | "suggestedStatus" | "owes" | "type" | "inactive">): Owes | null => {
  const t = openTabOf(it);
  return t === "ongoing" ? null : t;
};
/** Nobody has confirmed a status: "✦ suggested", and one of the "to confirm". An inactive item (X4) isn't: its chip says Inactive, and Re-open is its act. */
export const needsConfirm = (it: Pick<ItemRecord, "status" | "inactive" | "suggestedStatus">): boolean => it.status === null && !isInactive(it);

export type StatusTone = "amber" | "sky" | "plain" | "green" | "strike";
/** The mock's `ST` colours: To do amber, In progress sky, Done green, Dropped struck through. */
export const STATUS_TONE: Record<ActionItemStatus, StatusTone> = {
  not_started: "amber",
  in_progress: "sky",
  done: "green",
  dropped: "strike",
};

export interface StatusChip {
  key: ActionItemStatus;
  word: string;
  tone: StatusTone;
  /** Dashed: the model's suggestion, not confirmed. */
  ghost: boolean;
}

export function statusChip(it: Pick<ItemRecord, "status" | "suggestedStatus" | "inactive">): StatusChip {
  const key = effectiveStatus(it);
  // Inactive (X4) is the re-check's, not a status anyone sets: the page's muted chip, never dashed.
  if (isInactive(it)) return { key, word: INACTIVE_WORD, tone: "plain", ghost: false };
  return { key, word: STATUS_WORDS[key], tone: STATUS_TONE[key], ghost: needsConfirm(it) };
}

/** "We owe" / "Client owes": the list's tag. */
export const OWES_TAG: Record<Owes, string> = { us: "We owe", them: "Client owes" };
/** The form's and the history's words. */
export const OWES_WORDS: Record<Owes, string> = { us: "We owe the client", them: "The client owes us" };

// ─── The reading ─────────────────────────────────────────────────────────────

/** We owe · Client owes · Ongoing · Done · All · Archived (Ongoing by type). */
export type ItemsTab = "us" | "them" | "ongoing" | "done" | "all" | "archived";
const TABS: readonly ItemsTab[] = ["us", "them", "ongoing", "done", "all", "archived"];
/** The tabs before P2, so an address someone kept still lands on the tab that replaced it. */
const OLD_TABS: Record<string, ItemsTab> = { open: "us", waiting: "them", closed: "done" };
export const parseItemsTab = (v: string | undefined): ItemsTab => ((TABS as readonly string[]).includes(v ?? "") ? (v as ItemsTab) : (OLD_TABS[v ?? ""] ?? "us"));

/** The filter chips: only the ones to confirm, or only the escalations. */
export type ItemsOnly = "confirm" | "esc";
export const parseItemsOnly = (v: string | undefined): ItemsOnly | null => (v === "confirm" || v === "esc" ? v : null);

export type ItemsSort = "source" | "title" | "brand" | "owes" | "kind" | "assignee" | "status" | "opened" | "last";
const SORTS: readonly ItemsSort[] = ["source", "title", "brand", "owes", "kind", "assignee", "status", "opened", "last"];
export const parseItemsSort = (v: string | undefined): ItemsSort => ((SORTS as readonly string[]).includes(v ?? "") ? (v as ItemsSort) : "opened");
export const parseItemsDir = (v: string | undefined): 1 | -1 => (v === "desc" ? -1 : 1);

export interface ItemsReading {
  source?: "Slack" | "Fathom" | "Manual";
  pod: string | null;
  brand: string | null;
  tab: ItemsTab;
  only: ItemsOnly | null;
  /** Oldest first unless a column says otherwise. */
  sort: ItemsSort;
  dir: 1 | -1;
}

/** The page's address for a reading: only what differs from the default is in it. */
export function itemsQuery(r: ItemsReading): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  if (r.source) pairs.push(["source", r.source]);
  if (r.pod !== null) pairs.push(["pod", r.pod]);
  if (r.brand !== null) pairs.push(["brand", r.brand]);
  if (r.tab !== "us") pairs.push(["tab", r.tab]);
  if (r.only !== null) pairs.push(["only", r.only]);
  if (r.sort !== "opened") pairs.push(["sort", r.sort]);
  if (r.dir !== 1) pairs.push(["dir", "desc"]);
  return pairs;
}

/**
 * ⭐ WE OWE: the page's first tab, open work with the ball on our side (not
 * archived, not a standing rule, not inactive). The Operations Inbox's
 * action-item review lists exactly these, so its card and the tab agree.
 */
export const isWeOwe = (it: Pick<ItemRecord, "archived" | "status" | "suggestedStatus" | "owes" | "type" | "inactive">): boolean => !it.archived && openTabOf(it) === "us";

const inTab: Record<ItemsTab, (it: ItemRecord) => boolean> = {
  us: isWeOwe,
  them: (it) => !it.archived && openTabOf(it) === "them",
  ongoing: (it) => !it.archived && openTabOf(it) === "ongoing",
  done: (it) => !it.archived && isWork(it) && !isOpenStatus(it),
  all: (it) => !it.archived,
  archived: (it) => it.archived,
};
/**
 * ⭐ OPEN WORK: not archived, not done or dropped; open scheduling included and
 * done scheduling not (Q4). The scope bar's counts, and the one rule the Weekly
 * Report offers by (`reportActionPanel`, Add selected), so the two never differ.
 */
export const isOpenItem = (it: Pick<ItemRecord, "archived" | "status" | "suggestedStatus" | "inactive">): boolean => !it.archived && isOpenStatus(it) && !isInactive(it);

// ─── The list ────────────────────────────────────────────────────────────────

export interface ItemRow {
  origins?: string;
  id: string;
  href: string;
  title: string;
  /** The description's first line. */
  desc: string;
  escalation: boolean;
  /** "Scheduling" or "Standing", for an item that isn't a one-off task. */
  typeTag: string | null;
  brand: string;
  /** Who has the ball; null on a closed item or a standing rule (`openTabOf`), which shows no tag. */
  owes: Owes | null;
  owesTag: string | null;
  /** Its work type's label and its one line for the hover (`dim` for Other); null: none yet, "—". */
  kind: { label: string; what: string; dim: boolean } | null;
  /** The assignee's name, or null: "Unassigned". */
  assignee: string | null;
  status: StatusChip;
  /** "due Oct 14", red once it is past and the item isn't done or dropped. */
  due: { text: string; overdue: boolean } | null;
  /** "Oct 9 · 2d", and the whole time on hover. */
  opened: string;
  openedTitle: string;
  /** "3h ago", or "—" for an item with no Slack behind it. */
  last: string;
  /** The re-check's line in place of the description while its ending or merge holds (X4): "Ended: replaced by …". */
  recheck: string | null;
  /** Re-opened after it went inactive (X4): the row's tag. */
  reopened: boolean;
}

/** One item closed today, for the "Done here today" card (X4): by a person, or the re-check's suggestion. */
export interface DoneTodayRow {
  id: string;
  href: string;
  brand: string;
  title: string;
  /** "Re-check: ended" (✦), or "Priya Raman marked it done". */
  how: string;
  byModel: boolean;
  /** A person's own mark today, which Undo puts back. */
  undo: boolean;
}

/** One item in "Recently inactive" (X4). */
export interface InactiveRow {
  id: string;
  href: string;
  brand: string;
  title: string;
  /** "Quiet 12 working days". */
  quiet: string;
  /** The re-check's one line. */
  why: string;
  /** "Inactive today" / "Inactive since Oct 22". */
  when: string;
}

export interface ItemsColumn {
  key: ItemsSort;
  label: string;
  href: string;
  /** The sort shown: "↑", "↓", or "". */
  arrow: string;
}

export interface ItemsModel {
  pods: RtPill[];
  brands: RtPill[] | null;
  tabs: Array<{ key: ItemsTab; label: string; href: string; count: number }>;
  chips: Array<{ key: ItemsOnly; label: string; count: number; href: string; on: boolean }>;
  columns: ItemsColumn[];
  /** The body's one empty state, or null. */
  empty: { title: string; act: string; settings: boolean } | null;
  rows: ItemRow[];
  rowsEmpty: string | null;
  notes: FooterNote[];
  /** The cards under the list (X4), in scope: what closed today, and what went inactive in the last 14 days. */
  doneToday: DoneTodayRow[];
  recentlyInactive: InactiveRow[];
}

const TZ = "America/New_York";
const isoDay = new Intl.DateTimeFormat("en-CA", { timeZone: TZ });
const shortDay = new Intl.DateTimeFormat("en-US", { timeZone: TZ, month: "short", day: "numeric" });
const fullWhen = new Intl.DateTimeFormat("en-US", { timeZone: TZ, dateStyle: "medium", timeStyle: "short" });
/** A New York day (YYYY-MM-DD) at noon, so its words are that day's in New York. */
const dayDate = (d: string): Date => new Date(`${d}T12:00:00-05:00`);
const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const listOf = (names: string[]): string => (names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);
const firstLine = (s: string): string => s.split("\n").find((l) => l.trim() !== "")?.trim() ?? "";

/** New York's midnight before `now` (ms). */
function nyMidnightMs(now: number): number {
  const d = isoDay.format(new Date(now));
  for (const off of [4, 5]) {
    const t = Date.parse(`${d}T00:00:00-0${off}:00`);
    if (isoDay.format(new Date(t)) === d && isoDay.format(new Date(t - 1)) !== d) return t;
  }
  return Date.parse(`${d}T00:00:00-05:00`);
}

/** The re-check's line while its ending or merge holds (X4): "Ended: …", "Merged: …", "Not work: …". */
export function recheckLine(it: Pick<ItemRecord, "recheck" | "status">): string | null {
  const rc = it.recheck ?? null;
  if (rc === null || it.status !== null) return null;
  return `${rc.verdict === "merged" ? "Merged" : rc.verdict === "not_work" ? "Not work" : "Ended"}: ${rc.reason}`;
}

/** "due Oct 14", and whether it is past (a New York day before today) on an item still open. */
export function dueChip(it: Pick<ItemRecord, "due" | "status" | "suggestedStatus">, now: number): { text: string; overdue: boolean } | null {
  if (it.due === null || isClosedStatus(effectiveStatus(it))) return null;
  return { text: `due ${shortDay.format(dayDate(it.due))}`, overdue: it.due < isoDay.format(new Date(now)) };
}

export function actionItemsList(input: {
  /** Now, in ms. */
  now: number;
  brands: readonly RtBrand[];
  items: readonly ItemRecord[];
  reading: ItemsReading;
  href: (r: ItemsReading) => string;
  itemHref: (id: string) => string;
  /** A person's name for an email. */
  nameOf: (email: string) => string;
  podLabels: ReadonlyMap<string, string>;
  settings: boolean;
  /** Today's (New York) status marks by a person, by item: who, and whether it is theirs to undo (X4's "Done here today"). */
  closedToday?: ReadonlyMap<string, { by: string; undo: boolean }>;
}): ItemsModel {
  const { now, brands, reading, href } = input;
  const go = (o: Partial<ItemsReading>): string => href({ ...reading, ...o });
  const nameOfBrand = new Map(brands.map((b) => [b.slug, b.name]));
  const known = new Set(brands.map((b) => b.slug));
  const items = input.items.filter((it) => known.has(it.brandSlug) && (!reading.source || itemOrigins(it).includes(reading.source)));

  const countOf = (slug: string): number => items.filter((it) => it.brandSlug === slug && isOpenItem(it)).length;
  const { pods, brands: brandPills, podBrands } = slackScopePills({ brands, countOf, pod: reading.pod, brand: reading.brand, go: (o) => go({ ...o, only: null }) });
  const scoped = reading.brand === null ? podBrands : podBrands.filter((b) => b.slug === reading.brand);
  const inScope = new Set(scoped.map((b) => b.slug));
  const mine = items.filter((it) => inScope.has(it.brandSlug));

  const tabs = (
    [
      ["us", "We owe"],
      ["them", "Client owes"],
      ["ongoing", "Ongoing"],
      ["done", "Done"],
      ["all", "All"],
      ["archived", "Archived"],
    ] as Array<[ItemsTab, string]>
  ).map(([key, label]) => ({ key, label, href: go({ tab: key, only: null }), count: mine.filter(inTab[key]).length }));

  const inThisTab = mine.filter(inTab[reading.tab]);
  const toConfirm = inThisTab.filter(needsConfirm).length;
  const escalations = inThisTab.filter((it) => it.escalation).length;
  const chips = [
    { key: "confirm" as const, label: `${toConfirm.toLocaleString("en-US")} to confirm`, count: toConfirm, href: go({ only: reading.only === "confirm" ? null : "confirm" }), on: reading.only === "confirm" },
    { key: "esc" as const, label: plural(escalations, "escalation"), count: escalations, href: go({ only: reading.only === "esc" ? null : "esc" }), on: reading.only === "esc" },
  ];
  const shown = inThisTab.filter((it) => (reading.only === "confirm" ? needsConfirm(it) : reading.only === "esc" ? it.escalation : true));

  // ── Sorted: oldest first unless a column says otherwise; ties by when it was opened.
  const assigneeName = (it: ItemRecord): string | null => (it.assigneeEmail === null ? null : input.nameOf(it.assigneeEmail));
  const ORDER = Object.keys(STATUS_TONE) as ActionItemStatus[];
  // Standing rules and closed items (no ball) after both balls.
  const BALL_ORDER = { us: "0", them: "1" } as const;
  const key: Record<ItemsSort, (it: ItemRecord) => string | number> = {
    source: (it) => itemOrigins(it).join(" + "),
    title: (it) => it.title.toLowerCase(),
    brand: (it) => (nameOfBrand.get(it.brandSlug) ?? it.brandSlug).toLowerCase(),
    owes: (it) => { const b = rowBall(it); return b === null ? "2" : BALL_ORDER[b]; },
    // By its label; none yet last, either way round, as Unassigned.
    kind: (it) => { const k = shownKind(it); return k === null ? "￿" : KIND_WORDS[k].label.toLowerCase(); },
    // Unassigned last, either way round.
    assignee: (it) => (assigneeName(it) ?? "￿").toLowerCase(),
    status: (it) => ORDER.indexOf(effectiveStatus(it)),
    opened: (it) => it.createdAt,
    // No Slack behind it: as old as can be.
    last: (it) => it.lastActivityAt ?? it.lastSlackAt ?? 0,
  };
  const k = key[reading.sort];
  const sorted = shown.slice().sort((a, b) => {
    const x = k(a);
    const y = k(b);
    return (x > y ? 1 : x < y ? -1 : 0) * reading.dir || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1);
  });

  const columns: ItemsColumn[] = (
    [
      ["title", "Action item"],
      ["brand", "Brand"],
      ["source", "Source"],
      ["owes", "Owes"],
      ["kind", "Work type"],
      ["assignee", "Assignee"],
      ["status", "Status"],
      ["opened", "Opened"],
      ["last", "Last activity"],
    ] as Array<[ItemsSort, string]>
  ).map(([c, label]) => {
    const on = reading.sort === c;
    return { key: c, label, href: go({ sort: c, dir: on ? (reading.dir === 1 ? -1 : 1) : 1 }), arrow: on ? (reading.dir === 1 ? "↑" : "↓") : "" };
  });

  const rows: ItemRow[] = sorted.map((it) => {
    const age = Math.max(0, (now - it.createdAt) / 1000);
    const ball = rowBall(it);
    const kind = shownKind(it);
    return {
      id: it.id,
      href: input.itemHref(it.id),
      title: it.title,
      desc: firstLine(it.description),
      escalation: it.escalation,
      typeTag: it.type ? (TYPE_TAG[it.type] ?? null) : null,
      brand: nameOfBrand.get(it.brandSlug) ?? it.brandSlug,
      owes: ball,
      owesTag: ball === null ? null : OWES_TAG[ball],
      kind: kind === null ? null : { ...KIND_WORDS[kind], dim: kind === "other" },
      assignee: assigneeName(it),
      status: statusChip(it),
      due: dueChip(it, now),
      opened: `${shortDay.format(new Date(it.createdAt))} · ${dur(age)}`,
      openedTitle: `Opened ${fullWhen.format(new Date(it.createdAt))}${it.source === "model" ? " by the model" : ` by ${input.nameOf(it.createdBy)}`}`,
      origins: itemOrigins(it).join(" + "),
      last: (it.lastActivityAt ?? it.lastSlackAt) == null ? "—" : `${dur(Math.max(0, (now - (it.lastActivityAt ?? it.lastSlackAt ?? now)) / 1000))} ago`,
      recheck: recheckLine(it),
      reopened: (it.reopenedAt ?? null) !== null && !isInactive(it) && isOpenStatus(it),
    };
  });

  // ── The cards (X4): closed today, by a person or the re-check; inactive in the last 14 days. In scope, not archived.
  const todayStart = nyMidnightMs(now);
  const doneToday: DoneTodayRow[] = [];
  for (const it of mine) {
    if (it.archived || !isClosedStatus(effectiveStatus(it))) continue;
    const person = input.closedToday?.get(it.id);
    const rc = it.recheck ?? null;
    if (person !== undefined)
      doneToday.push({ id: it.id, href: input.itemHref(it.id), brand: nameOfBrand.get(it.brandSlug) ?? it.brandSlug, title: it.title, how: `${person.by} marked it ${STATUS_WORDS[effectiveStatus(it)].toLowerCase()}`, byModel: false, undo: person.undo });
    else if (rc !== null && it.status === null && rc.at >= todayStart)
      doneToday.push({ id: it.id, href: input.itemHref(it.id), brand: nameOfBrand.get(it.brandSlug) ?? it.brandSlug, title: it.title, how: rc.verdict === "merged" ? "Re-check: merged" : "Re-check: ended", byModel: true, undo: false });
  }
  const recentlyInactive: InactiveRow[] = mine
    .filter((it) => !it.archived && isInactive(it) && now - (it.inactive as NonNullable<ItemRecord["inactive"]>).at <= RECENTLY_INACTIVE_DAYS * 86_400_000)
    .sort((a, b) => (b.inactive?.at ?? 0) - (a.inactive?.at ?? 0))
    .map((it) => {
      const ia = it.inactive as NonNullable<ItemRecord["inactive"]>;
      return {
        id: it.id,
        href: input.itemHref(it.id),
        brand: nameOfBrand.get(it.brandSlug) ?? it.brandSlug,
        title: it.title,
        quiet: `Quiet ${plural(Math.round(ia.quietDays), "working day")}`,
        why: capitalised(ia.why),
        when: ia.at >= todayStart ? "Inactive today" : `Inactive since ${shortDay.format(new Date(ia.at))}`,
      };
    });

  // ── The footer: brands the model can't open items for.
  const notes: FooterNote[] = [];
  const readable = scoped.filter((b) => b.word === "reading" || b.word === "still-reading");
  const dark = scoped.filter((b) => b.word === "not-set" || b.word === "bot-missing");
  if (dark.length > 0 && readable.length > 0) {
    notes.push({
      text: `${plural(dark.length, "brand")} in this scope ${dark.length === 1 ? "has" : "have"} no Slack channel the YWS bot can read, so no Slack items are opened for ${dark.length === 1 ? "it" : "them"}: ${listOf(dark.map((b) => b.name))}. Meeting follow-ups and items made by hand still show.`,
      defect: true,
    });
  }

  const blank = { pods, brands: brandPills, tabs, chips, columns, rows: [], rowsEmpty: null, notes, doneToday, recentlyInactive };
  // A brand picked with no channel the bot reads is one empty state, as on the
  // other two pages, UNLESS someone has made items for it by hand: those are
  // work owed, and hiding them behind "ask an admin" would lose them.
  const picked = reading.brand === null ? null : (brands.find((b) => b.slug === reading.brand) ?? null);
  if (picked !== null && picked.word !== "reading" && picked.empty !== null && mine.length === 0) {
    return { ...blank, notes: [], empty: { ...picked.empty, settings: input.settings && picked.word !== "still-reading" } };
  }
  if (picked === null && readable.length === 0 && mine.length === 0) {
    const scopeWords =
      reading.pod === null ? "None of the brands" : reading.pod === "none" ? "None of the brands nobody leads" : `None of the brands in ${input.podLabels.get(reading.pod) ?? "this pod"}`;
    return {
      ...blank,
      notes: [],
      empty: {
        title: `${scopeWords} has a Slack channel the YWS bot can read.`,
        act: "The model opens action items from a brand's client channel: ask an admin to link it on Settings → Brands. Add one by hand with + New action item.",
        settings: input.settings,
      },
    };
  }

  const tabWords: Record<ItemsTab, string> = {
    us: "No action items we owe",
    them: "No action items the client owes",
    ongoing: "No standing rules",
    done: "No action items done or dropped",
    all: "No action items",
    archived: "No action items archived",
  };
  const rowsEmpty =
    rows.length > 0
      ? null
      : reading.only === "confirm"
        ? "Nothing here to confirm."
        : reading.only === "esc"
          ? "No escalations here."
          : `${tabWords[reading.tab]}${reading.brand !== null ? " for this brand" : ""}.`;

  return { ...blank, empty: null, rows, rowsEmpty };
}

// ─── The popup ───────────────────────────────────────────────────────────────

/**
 * WHO OPENED AN ITEM THE ITEMS READ DIDN'T: the Missed Items
 * check, said where the popup says who opened it and on the History tab's
 * opening entry, from the opening row's `addedBy`. Words only, no new mark.
 */
export const CHECK_OPENED_WORDS = "Added by the Missed Items check";
/** Whether the item's opening row says the Missed Items check added it. */
export const addedByCheck = (rows: ReadonlyArray<Pick<HistoryRow, "kind" | "verb" | "toValue">>): boolean =>
  rows.some((r) => r.kind === BRAND_KIND && r.verb === "create" && parse(r.toValue)?.addedBy === ITEMS_CHECK_ADDED_BY);

/** Where a field's words came from: "✦ AI-written" until a person changes them, then "Edited". */
export type Provenance = "ai" | "edited" | null;

/** An audit row, as the History tab and the provenance need it (lib/server/audit-db.ts `AuditEventRow`). */
export interface HistoryRow {
  eventId: string;
  at: number;
  actorEmail: string;
  kind: string;
  verb: string;
  fromValue: string | null;
  toValue: string | null;
}

const isModelRow = (r: Pick<HistoryRow, "actorEmail">): boolean => r.actorEmail === ACTION_ITEM_MODEL_EMAIL;

/**
 * "✦ AI-written" on a field the model wrote and no person has changed since;
 * "Edited" once someone has. A hand-made item's fields are a person's from the
 * start: nothing, until someone edits one.
 */
export function provenance(item: Pick<ItemRecord, "source">, rows: readonly HistoryRow[], kind: string): Provenance {
  const byPerson = rows.some((r) => r.kind === kind && !isModelRow(r) && (item.source === "model" || r.verb === "update"));
  if (byPerson) return "edited";
  return item.source === "model" && rows.some((r) => r.kind === kind && isModelRow(r)) ? "ai" : null;
}

export interface ItemPopupModel {
  title: string;
  brand: string;
  escalation: boolean;
  /** "Standing" or "Scheduling", as on the row: the popup's head says where a saved type put it (P5). */
  typeTag: string | null;
  status: StatusChip;
  /** The model's suggestion with its reason and Confirm, while nobody has confirmed one. */
  suggestion: { status: ActionItemStatus; word: string; why: string | null } | null;
  /** `kind`: "✦ Suggested" while it is the Work Type skill's, "Edited" once a person's (its history row is theirs alone). */
  prov: { title: Provenance; description: Provenance; assignee: Provenance; kind: Provenance };
  /** The assignee picker: staff in the app, and whoever is assigned now if they aren't. */
  staff: Array<{ email: string; name: string }>;
  archived: boolean;
  /** "Opened by the model, Oct 9" / "Added by the Missed Items check, Oct 9" / "Made by Stefan, Oct 9". */
  opened: string;
  /** The re-check's banner (X4): inactive, with Re-open; or re-opened, and when. Null otherwise. */
  quiet: { text: string; reopen: boolean } | null;
}

export function actionItemPopup(
  item: ItemRecord,
  input: { brandName: string; rows: readonly HistoryRow[]; staff: ReadonlyArray<{ email: string; fullName: string }>; nameOf: (email: string) => string },
): ItemPopupModel {
  const staff = input.staff.map((s) => ({ email: s.email, name: s.fullName }));
  if (item.assigneeEmail !== null && !staff.some((s) => s.email === item.assigneeEmail)) staff.unshift({ email: item.assigneeEmail, name: input.nameOf(item.assigneeEmail) });
  return {
    title: item.title,
    brand: input.brandName,
    escalation: item.escalation,
    typeTag: item.type ? (TYPE_TAG[item.type] ?? null) : null,
    status: statusChip(item),
    // A legacy `waiting` or `ongoing` suggestion is offered as In progress: Confirm must put a status the save takes.
    suggestion:
      needsConfirm(item) && item.suggestedStatus !== null
        ? { status: effectiveStatus(item), word: STATUS_WORDS[effectiveStatus(item)], why: item.suggestedWhy }
        : null,
    prov: {
      title: provenance(item, input.rows, PERSON_FIELDS.title),
      description: provenance(item, input.rows, PERSON_FIELDS.description),
      assignee: provenance(item, input.rows, PERSON_FIELDS.assigneeEmail),
      kind: item.kindBy === "model" ? "ai" : item.kindBy === "person" ? "edited" : null,
    },
    staff,
    archived: item.archived,
    opened: `${item.source !== "model" ? `Made by ${input.nameOf(item.createdBy)}` : addedByCheck(input.rows) ? CHECK_OPENED_WORDS : "Opened by the model"}, ${fullWhen.format(new Date(item.createdAt))}`,
    quiet: quietBanner(item, input.rows, input.nameOf),
  };
}

/**
 * The popup's banner (X4), two plain sentences at most: an inactive item's
 * ("Inactive since Oct 26: quiet 12 working days and 7 of the brand's
 * conversations since. Nothing shows it done or dropped."), with Re-open; a
 * re-opened one's ("Re-opened by Maya Ortiz, Oct 26. It won't go inactive
 * again until it goes quiet once more."), while it is open.
 */
export function quietBanner(item: Pick<ItemRecord, "inactive" | "reopenedAt" | "status" | "suggestedStatus">, rows: ReadonlyArray<Pick<HistoryRow, "kind" | "verb" | "toValue" | "actorEmail" | "at">>, nameOf: (email: string) => string): { text: string; reopen: boolean } | null {
  const ia = item.inactive ?? null;
  if (ia !== null) {
    return {
      text: `Inactive since ${shortDay.format(new Date(ia.at))}: quiet ${plural(Math.round(ia.quietDays), "working day")} and ${plural(ia.since, "of the brand's conversations", "of the brand's conversations")} since. Nothing shows it done or dropped.`,
      reopen: true,
    };
  }
  if ((item.reopenedAt ?? null) === null || !isOpenStatus(item)) return null;
  const r = rows.filter((x) => x.kind === INACTIVE_KIND && x.verb === "update" && parse(x.toValue)?.inactive === false).at(-1);
  const who = r === undefined ? "someone" : nameOf(r.actorEmail);
  return { text: `Re-opened by ${who}, ${shortDay.format(new Date(item.reopenedAt as number))}. It won't go inactive again until it goes quiet once more.`, reopen: false };
}

// ─── The popup's Previous and Next ──────────────────────────────────────────

/**
 * ⭐ PREVIOUS AND NEXT WALK THE LIST AS IT WAS WHEN THE POPUP OPENED: its ids,
 * kept for the browser tab (`list` is the list's own address, so another
 * reading starts afresh). An item confirmed or archived leaves the list behind
 * the popup and keeps its place here, so Next after Confirm goes to the item
 * that followed it, and Previous comes back to it. The list drawn with no
 * popup open forgets it.
 */
export interface PagerSnapshot {
  list: string;
  ids: string[];
}

/**
 * The walk for an item's popup: the one kept, if it is this list's and holds
 * the item; else this list as drawn now, with the item first when it is not on
 * it (opened from a link, or from a card), so Next goes to the list's first.
 */
export function pagerSnapshot(kept: PagerSnapshot | null, list: string, id: string, listIds: readonly string[]): PagerSnapshot {
  if (kept !== null && kept.list === list && kept.ids.includes(id)) return kept;
  return { list, ids: listIds.includes(id) ? [...listIds] : [id, ...listIds] };
}

/** Stands for the item's id in the address the popup's Previous and Next fill in. */
export const ITEM_SLOT = "__item__";

/** The items before and after `id` on the walk; null at either end. */
export function pagerNeighbours(ids: readonly string[], id: string): { prev: string | null; next: string | null; at: number; of: number } {
  const i = ids.indexOf(id);
  if (i < 0) return { prev: null, next: null, at: 0, of: ids.length };
  return { prev: ids[i - 1] ?? null, next: ids[i + 1] ?? null, at: i + 1, of: ids.length };
}

/** A kept walk read back from storage, its shape checked; null for anything else. */
export function pagerSnapshotOf(raw: string | null): PagerSnapshot | null {
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw) as { list?: unknown; ids?: unknown } | null;
    return v !== null && typeof v.list === "string" && Array.isArray(v.ids) && v.ids.every((x) => typeof x === "string") ? { list: v.list, ids: v.ids as string[] } : null;
  } catch {
    return null;
  }
}

// ─── The History tab ─────────────────────────────────────────────────────────

/** One field's change in an entry: the old value struck through above the new. `from` undefined: a first value, no old one. */
export interface HistoryChange {
  field: string;
  from?: { text: string; empty: boolean };
  to: { text: string; empty: boolean };
}

export interface HistoryEntry {
  key: string;
  /** "✦ Model", or the person's name. */
  who: string;
  model: boolean;
  when: string;
  /** What happened, in a line: "Opened from Slack", "Confirmed the status", "Edited". */
  what: string;
  /** The model's reason for its suggestion, where it gave one: "Why Done: …". */
  note: string | null;
  changes: HistoryChange[];
}

/** Each kind's field name, as the form labels it. */
const FIELD_LABEL: Record<string, string> = {
  [PERSON_FIELDS.title]: "Title",
  [PERSON_FIELDS.description]: "Description",
  [PERSON_FIELDS.status]: "Status",
  [PERSON_FIELDS.assigneeEmail]: "Assignee",
  [PERSON_FIELDS.owes]: "Who owes",
  [PERSON_FIELDS.due]: "Due",
  [PERSON_FIELDS.inReport]: "Weekly Report",
  [PERSON_FIELDS.notes]: "Notes",
  [PERSON_FIELDS.archived]: "Archived",
  [PERSON_FIELDS.type]: "Type",
  [PERSON_FIELDS.kind]: "Work type",
  [ESCALATION_KIND]: "Escalation",
  [SUGGESTION_KIND]: "Suggested status",
};
/** The order a first entry lists its fields in: the form's. */
const FIELD_ORDER = [...Object.values(PERSON_FIELDS), ESCALATION_KIND, SUGGESTION_KIND];

const parse = (v: string | null): Record<string, unknown> | null => {
  if (v === null) return null;
  try {
    const o = JSON.parse(v) as unknown;
    return o !== null && typeof o === "object" ? (o as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/** One payload, in words. Empty is a value too ("empty", in italics). */
function shown(kind: string, payload: Record<string, unknown> | null, nameOf: (email: string) => string): { text: string; empty: boolean } {
  const v = payload === null ? null : Object.values(payload)[0];
  const word = (t: string | null): { text: string; empty: boolean } => (t === null || t === "" ? { text: "empty", empty: true } : { text: t, empty: false });
  switch (kind) {
    case PERSON_FIELDS.status:
      return v == null ? { text: "Not confirmed", empty: true } : word(STATUS_WORDS[v as ActionItemStatus] ?? String(v));
    case SUGGESTION_KIND:
      return payload === null ? word(null) : word(STATUS_WORDS[payload.status as ActionItemStatus] ?? String(payload.status));
    case PERSON_FIELDS.assigneeEmail:
      return v == null ? { text: "Unassigned", empty: true } : word(nameOf(String(v)));
    case PERSON_FIELDS.owes:
      return word(OWES_WORDS[v as Owes] ?? (v == null ? null : String(v)));
    case PERSON_FIELDS.due:
      return v == null ? word(null) : word(shortDay.format(dayDate(String(v))) + `, ${String(v).slice(0, 4)}`);
    case PERSON_FIELDS.inReport:
      return word(v === true ? "Shown this week" : "Not shown");
    case PERSON_FIELDS.archived:
      return word(v === true ? "Archived" : "Not archived");
    case PERSON_FIELDS.type:
      return word(TYPE_WORDS[v as ItemType] ?? (v == null ? null : String(v)));
    case PERSON_FIELDS.kind:
      return word(v == null ? null : (KIND_WORDS[v as WorkKind]?.label ?? String(v)));
    case ESCALATION_KIND:
      return word(v === true ? "An escalation" : "Not an escalation");
    default:
      return word(v == null ? null : String(v));
  }
}

/**
 * An item's history, newest first: one entry per act (one actor, one moment),
 * each field it changed with the old value and the new. The first entry is the
 * opening, every field it was opened with and no old values. Nothing here
 * restores a value: the old one is there to copy.
 */
export function itemHistory(rows: readonly HistoryRow[], o: { nameOf: (email: string) => string; channelName: (channelId: string) => string }): HistoryEntry[] {
  const groups = new Map<string, HistoryRow[]>();
  for (const r of rows) {
    const k = `${r.actorEmail}\u0000${r.at}`;
    const g = groups.get(k);
    if (g) g.push(r);
    else groups.set(k, [r]);
  }
  const entries: HistoryEntry[] = [];
  for (const [k, g] of groups) {
    const first = g[0];
    const model = isModelRow(first);
    const opening = g.some((r) => r.kind === BRAND_KIND);
    const fields = g.filter((r) => FIELD_LABEL[r.kind] !== undefined).sort((a, b) => FIELD_ORDER.indexOf(a.kind) - FIELD_ORDER.indexOf(b.kind));
    const linked = g.filter((r) => r.kind === MESSAGES_KIND).map((r) => parse(r.toValue));
    const linkedCount = linked.reduce((n, p) => n + (Array.isArray(p?.ts) ? (p?.ts as unknown[]).length : 0), 0);
    const linkedWhere = listOf(Array.from(new Set(linked.map((p) => `#${o.channelName(String(p?.channelId ?? ""))}`))));
    const suggestion = g.find((r) => r.kind === SUGGESTION_KIND);
    // The model's reason, said as the reason for its suggestion: under an opening it would otherwise read as the item's.
    const reason = suggestion ? ((parse(suggestion.toValue)?.why as string | null | undefined) ?? null) : null;
    const why = suggestion && reason ? `Why ${shown(SUGGESTION_KIND, parse(suggestion.toValue), o.nameOf).text}: ${reason}` : null;

    const changes: HistoryChange[] = fields.map((r) => {
      const to = shown(r.kind, parse(r.toValue), o.nameOf);
      return r.verb === "create" || r.fromValue === null ? { field: FIELD_LABEL[r.kind], to } : { field: FIELD_LABEL[r.kind], from: shown(r.kind, parse(r.fromValue), o.nameOf), to };
    });

    // The re-check (X4): its verdict and its one line, and an item marked inactive or re-opened. Words in the model, never JSX.
    // One entry per re-check row: one run can both keep an item and merge a later one into it, the same moment by the same actor.
    const rcs = g.filter((r) => r.kind === RECHECK_KIND);
    const ia = g.find((r) => r.kind === INACTIVE_KIND);
    if (rcs.length > 0 || ia !== undefined) {
      for (const row of ia !== undefined ? [ia, ...rcs] : rcs) {
        const x = parse(row.toValue) ?? {};
        const q = typeof x.quietDays === "number" ? `${Math.round(x.quietDays)} working day${Math.round(x.quietDays) === 1 ? "" : "s"}` : null;
        let w: string;
        let n: string | null = null;
        const ch: HistoryChange[] = [];
        if (row === ia) {
          const on = x.inactive === true;
          w = on ? "Marked inactive" : "Re-opened";
          // X4b: marked on the clock, the read it rests on named, so nobody looks for a call that wasn't made.
          const clock = typeof x.onClock === "number" ? ` Nothing new in the channel since the re-check of ${shortDay.format(new Date(x.onClock))} read it as gone quiet, so it wasn't read again.` : "";
          if (on) n = `Quiet ${q ?? "a while"}, and ${typeof x.since === "number" ? x.since : "several"} of the brand's conversations since never mention it.${clock} ${capitalised(String(x.why ?? ""))}`.trim();
          ch.push(on ? { field: "Status", to: { text: INACTIVE_WORD, empty: false } } : { field: "Status", from: { text: INACTIVE_WORD, empty: false }, to: { text: "Back on the list", empty: false } });
        } else if (x.verdict === "merged") {
          w = `Merged into “${String(x.intoTitle ?? "an earlier item")}”, its messages moved there`;
          n = capitalised(String(x.reason ?? "")) || null;
        } else if (x.verdict === "merged_from") {
          w = `“${String(x.fromTitle ?? "A later item")}” merged into this one${typeof x.moved === "number" && x.moved > 0 ? `, with its ${x.moved === 1 ? "message" : `${x.moved} messages`}` : ""}`;
          n = capitalised(String(x.reason ?? "")) || null;
        } else if (x.verdict === "ended" || x.verdict === "not_work") {
          const word = x.closeAs === "dropped" || x.verdict === "not_work" ? "Dropped" : "Done";
          w = `Re-checked${q ? ` after ${q} quiet` : ""}, and suggested ${word}`;
          n = `${x.verdict === "not_work" ? "Not work" : "Ended"}: ${String(x.reason ?? "")}`;
        } else {
          w = `Re-checked${q ? ` after ${q} quiet` : ""}: no sign it ended, kept open`;
          n = capitalised(String(x.reason ?? "")) || null;
        }
        entries.push({ key: row === (ia ?? rcs[0]) ? k : `${k}:${row.eventId}`, who: model ? "✦ Model" : o.nameOf(first.actorEmail), model, when: fullWhen.format(new Date(first.at)), what: w, note: n, changes: ch });
      }
      continue;
    }

    let what: string;
    const msgWords = (verb: string) => `${verb} ${linkedCount === 1 ? "a Slack message" : `${linkedCount} Slack messages`} in ${linkedWhere}`;
    if (opening) {
      const from = `${linkedCount === 1 ? "a message" : `${linkedCount} messages`} in ${linkedWhere}`;
      const byCheck = model && addedByCheck(g);
      what = byCheck ? (linkedCount > 0 ? `${CHECK_OPENED_WORDS}, from ${from}` : CHECK_OPENED_WORDS) : model ? (linkedCount > 0 ? `Opened from ${from}` : "Opened") : linkedCount > 0 ? `Made by hand, from ${linkedCount === 1 ? "a message" : `${linkedCount} messages`} in ${linkedWhere}` : "Made by hand";
    } else {
      const parts: string[] = [];
      const person = fields.filter((r) => r.kind !== SUGGESTION_KIND && r.kind !== ESCALATION_KIND);
      const only = (kind: string) => person.length === 1 && person[0].kind === kind;
      // The model moving the ball (items v4) is a turn in Slack, not an edit: it says who owes now.
      const ball = model && only(PERSON_FIELDS.owes) ? OWES_WORDS[parse(person[0].toValue)?.owes as Owes] : undefined;
      if (ball !== undefined) parts.push(`${ball} now`);
      else if (only(PERSON_FIELDS.status) && parse(person[0].fromValue)?.status == null) parts.push("Confirmed the status");
      else if (only(PERSON_FIELDS.archived)) parts.push(parse(person[0].toValue)?.archived === true ? "Archived" : "Unarchived");
      else if (person.length > 0) parts.push("Edited");
      // A suggestion updated to nothing is a person's status clearing it, in the same act.
      if (suggestion) parts.push(suggestion.toValue === null ? "Cleared the model's suggestion" : `Suggested ${shown(SUGGESTION_KIND, parse(suggestion.toValue), o.nameOf).text}`);
      const esc = g.find((r) => r.kind === ESCALATION_KIND);
      if (esc) parts.push(parse(esc.toValue)?.escalation === true ? "Flagged it an escalation" : "Took it off escalation");
      if (linkedCount > 0) parts.push(msgWords("Linked"));
      what = parts.length > 0 ? parts.map((p, i) => (i === 0 ? p : p[0].toLowerCase() + p.slice(1))).join(", and ") : "Changed";
    }
    entries.push({ key: k, who: model ? "✦ Model" : o.nameOf(first.actorEmail), model, when: fullWhen.format(new Date(first.at)), what, note: why, changes });
  }
  return entries.reverse();
}

// ─── Where it stands: the ledger ─────────────────────────────────────────────

/** One ledger row, as lib/server/action-items.ts `itemEvidence` reads it. */
export interface LedgerEntry {
  placeId: string;
  ts: string;
  status: ActionItemStatus | null;
  author: string | null;
  text: string | null;
  channel: string | null;
}

export interface LedgerLine {
  key: string;
  status: ActionItemStatus;
  word: string;
  tone: StatusTone;
  /** "Oct 9, 3:04 PM", New York. */
  when: string;
  /** "Acsah Solis in #alpha-yws", or "A message in #alpha-yws" once it is deleted or unknown. */
  who: string;
  /** The message's first line, clipped: what moved it. Null when it is no longer stored. */
  text: string | null;
}

const TEXT_MAX = 140;
const whenWords = new Intl.DateTimeFormat("en-US", { timeZone: TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/**
 * The item's status as the model's reads moved it, oldest first: each message
 * whose status differs from the one before (a restated status is not a step,
 * C12), from any conversation. `messages` is how many messages bear on it
 * in all, for the line under them. Empty for an item no v3 read has touched.
 * `words` turns a message's Slack markup into words (the page passes
 * lib/client-slack-prompts.ts `plainText`).
 */
export function ledgerTimeline(entries: readonly LedgerEntry[], words: (text: string) => string = (t) => t): { lines: LedgerLine[]; messages: number } {
  const lines: LedgerLine[] = [];
  let last: ActionItemStatus | null = null;
  for (const e of entries) {
    if (e.status === null || e.status === last) continue;
    last = e.status;
    const where = e.channel ? ` in #${e.channel}` : "";
    // Slack's markup as words (a mention as @Name), then its first line.
    const line = e.text === null ? null : firstLine(words(e.text));
    lines.push({
      key: `${e.placeId} ${e.ts}`,
      status: e.status,
      word: STATUS_WORDS[e.status],
      tone: STATUS_TONE[e.status],
      when: whenWords.format(new Date(Number(e.ts) * 1000)),
      who: e.author ? `${e.author}${where}` : `A message${where}`,
      text: line === null || line === "" ? null : line.length > TEXT_MAX ? `${line.slice(0, TEXT_MAX - 1).trimEnd()}…` : line,
    });
  }
  return { lines, messages: entries.length };
}

// ─── The form ────────────────────────────────────────────────────────────────

/** The form's fields, as strings, in the order the form lays them out: what Save compares and sends. */
export const FORM_FIELDS = ["title", "description", "status", "assignee", "owes", "due", "type", "kind", "notes"] as const;
export type FormField = (typeof FORM_FIELDS)[number];
export type FormValues = Record<FormField, string>;

/**
 * An item's values, as the form holds them ("" for none, "1" for a ticked box).
 * Both the form's start and what Save compares come from here, so a field is
 * sent only if the person changes it. An item with no type is a task. Its work type is the stored one, the skill's or a person's,
 * "" for none yet, so it is sent only if the person picks one.
 */
export function formValues(it: Pick<ItemRecord, "title" | "description" | "status" | "assigneeEmail" | "owes" | "due" | "notes" | "type" | "kind">): FormValues {
  return {
    title: it.title,
    description: it.description,
    status: it.status ?? "",
    assignee: it.assigneeEmail ?? "",
    owes: it.owes,
    due: it.due ?? "",
    type: it.type ?? "task",
    kind: it.kind ?? "",
    notes: it.notes,
  };
}

/** Which fields differ: Save's "Unsaved changes", and what it sends. */
export function changedFields(a: FormValues, b: FormValues): FormField[] {
  return FORM_FIELDS.filter((f) => a[f] !== b[f]);
}

export const itemOrigins = (it: Pick<ItemRecord, "origins" | "source">): Array<"Slack" | "Fathom" | "Manual"> => it.origins ?? [it.source === "manual" ? "Manual" : "Slack"];
